/*
 * Shop grid (components/coffee/ShopGrid.tsx): category tabs, taste filters, chips, sort and the
 * empty state, over the product cards that sections/main-collection.liquid renders.
 * ?category=<slug> (or the older ?method=filter) picks the first tab, names it in the page hero
 * and scrolls to the products; the URL then follows the tab (?category=…), as on the Next.js site.
 */
(() => {
  const legacy = { filter: "filter-coffee" };
  const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const plural = (n) => `${n} ${n === 1 ? "coffee" : "coffees"}`;

  function setup(root) {
    if (root.__shop) return;
    root.__shop = true;
    const $$ = (s) => Array.from(root.querySelectorAll(s));
    const tabs = $$("[data-shop-tab]");
    const tastesEls = $$("[data-shop-taste]");
    const grid = root.querySelector("[data-shop-grid]");
    const cards = $$("[data-shop-card]");
    const empty = root.querySelector("[data-shop-empty]");
    const sortEl = root.querySelector("[data-shop-sort]");
    const chips = root.querySelector("[data-shop-chips]");
    const chipList = root.querySelector("[data-shop-chip-list]");
    const chipTpl = root.querySelector("template[data-shop-chip]");
    const heading = root.querySelector("[data-shop-heading]");
    const counts = $$("[data-shop-count]");
    const order = new Map(cards.map((c, i) => [c, i]));

    // Initial choice from the URL (page.tsx + ShopGrid's `start`).
    const params = new URLSearchParams(window.location.search);
    const method = params.get("method") || undefined;
    const requested = params.get("category") || (method === "filter" ? "filter-coffee" : undefined);
    const tabFor = (slug) => tabs.find((t) => t.dataset.shopTab === slug && slug !== "all");
    const initialTab = requested ? tabFor(requested) : undefined;
    const start = initialTab ? requested : method ? legacy[method] : undefined;

    let cat = start && tabFor(start) ? start : "all";
    let tastes = [];
    let sort = "featured";

    // The page hero names the category the visitor arrived with (client brief item 26).
    if (initialTab) {
      const hero = document.querySelector("[data-page-hero]");
      if (hero) {
        const title = hero.querySelector("[data-hero-title]");
        const accent = hero.querySelector("[data-hero-accent]");
        const intro = hero.querySelector("[data-hero-intro]");
        if (title) title.textContent = initialTab.dataset.label || initialTab.textContent.trim();
        if (accent) accent.remove();
        if (intro) {
          intro.textContent = initialTab.dataset.intro || "";
          intro.hidden = !initialTab.dataset.intro;
        }
      }
    }

    const syncUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("method");
      url.searchParams.delete("collection");
      if (cat === "all") url.searchParams.delete("category");
      else url.searchParams.set("category", cat);
      window.history.replaceState(window.history.state, "", url);
    };

    const paintTabs = () => {
      tabs.forEach((t) => {
        const on = t.dataset.shopTab === cat;
        t.setAttribute("aria-selected", String(on));
        t.classList.remove(...(on ? t.dataset.off : t.dataset.on).split(" ").filter(Boolean));
        t.classList.add(...(on ? t.dataset.on : t.dataset.off).split(" ").filter(Boolean));
      });
    };

    const paintChips = () => {
      if (!chips) return;
      chipList.innerHTML = "";
      tastes.forEach((key) => {
        const input = tastesEls.find((i) => i.value === key);
        const label = (input && input.dataset.label) || key;
        const node = chipTpl.content.firstElementChild.cloneNode(true);
        node.querySelector("[data-chip-label]").textContent = label;
        node.querySelector("[data-chip-x]").setAttribute("aria-label", `Remove ${label}`);
        node.addEventListener("click", () => {
          tastes = tastes.filter((x) => x !== key);
          if (input) input.checked = false;
          render();
        });
        chipList.appendChild(node);
      });
      chips.hidden = tastes.length === 0;
    };

    const render = () => {
      let list = cards.filter((c) => {
        if (cat !== "all" && c.dataset.category !== cat) return false;
        if (tastes.length && !(c.dataset.families || "").split(/\s+/).some((f) => tastes.includes(f))) return false;
        return true;
      });
      const price = (c) => Number(c.dataset.price || 0);
      const stable = (a, b) => order.get(a) - order.get(b);
      if (sort === "price-asc") list.sort((a, b) => price(a) - price(b) || stable(a, b));
      else if (sort === "price-desc") list.sort((a, b) => price(b) - price(a) || stable(a, b));
      else if (sort === "name") list.sort((a, b) => a.dataset.title.localeCompare(b.dataset.title) || stable(a, b));
      else list.sort((a, b) => Number(b.dataset.featured || 0) - Number(a.dataset.featured || 0) || stable(a, b));

      const shown = new Set(list);
      cards.forEach((c) => {
        c.hidden = !shown.has(c);
      });
      list.forEach((c, i) => {
        grid.appendChild(c);
        c.style.animationDelay = `${Math.min(i, 6) * 0.05}s`;
        c.classList.remove("rise-in");
        void c.offsetWidth;
        c.classList.add("rise-in");
      });
      grid.hidden = list.length === 0;
      if (empty) empty.hidden = list.length !== 0;

      const current = tabs.find((t) => t.dataset.shopTab === cat);
      if (heading && current) heading.textContent = current.dataset.label || current.textContent.trim();
      counts.forEach((el) => (el.textContent = plural(list.length)));
      paintTabs();
      paintChips();
    };

    tabs.forEach((t) =>
      t.addEventListener("click", () => {
        if (cat === t.dataset.shopTab) return;
        cat = t.dataset.shopTab;
        syncUrl();
        render();
      }),
    );
    tastesEls.forEach((input) =>
      input.addEventListener("change", () => {
        tastes = input.checked ? [...tastes.filter((x) => x !== input.value), input.value] : tastes.filter((x) => x !== input.value);
        render();
      }),
    );
    sortEl &&
      sortEl.addEventListener("change", () => {
        sort = sortEl.value;
        render();
      });
    const clearTastes = () => {
      tastes = [];
      tastesEls.forEach((i) => (i.checked = false));
    };
    root.querySelector("[data-shop-clear]")?.addEventListener("click", () => {
      clearTastes();
      render();
    });
    root.querySelector("[data-shop-reset]")?.addEventListener("click", () => {
      clearTastes();
      if (cat !== "all") {
        cat = "all";
        syncUrl();
      }
      render();
    });

    // Restore what the browser kept in the form controls (Back button).
    if (sortEl) sort = sortEl.value || "featured";
    tastes = tastesEls.filter((i) => i.checked).map((i) => i.value);

    syncUrl();
    render();

    // Arriving from the Coffee menu (?category=...): go straight to the products (item 26).
    if (initialTab || method) {
      const shop = document.getElementById("shop");
      if (shop) requestAnimationFrame(() => shop.scrollIntoView({ behavior: reduceMotion() ? "auto" : "smooth", block: "start" }));
    }
  }

  const init = (scope = document) => scope.querySelectorAll("[data-shop]").forEach(setup);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => init());
  else init();
  document.addEventListener("shopify:section:load", (e) => init(e.target));
})();
